import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { ChevronRightRounded, ArrowBack } from '@material-ui/icons';
import { Button, IconButton, List, ListItem, ListItemText, Tooltip, Box } from '@material-ui/core';
import _e from 'common/localeUtil';
import TemplateRevisionStatusComponent from './TemplateRevisionStatusComponent';
import EditorLeftPanelStyle from './EditorLeftPanelStyle';
import { EditorLeftPanelContext } from './EditorLeftPanelContext';
import ChangeBackgroundDialog from './dialog/ChangeBackgroundDialog';
import GMButton from './button/GMButton';
import {
	addImageComponentInsideEditor,
	addTextComponentInsideEditor,
	getResizeImageUrlWidth,
	hideSrcFromImageGrapesJSComponent,
	setStyleGrapesJSComponent,
} from '../utils';
import EditorLeftPanelResetTemplate from './EditorLeftPanelResetTemplate';
import EditorLeftPanelPreview from './EditorLeftPanelPreview';
import ReportsPreviewDialog from './dialog/ReportsPreviewDialog';
import { BRANDING_TYPE } from '../constants';
import EditorLeftPanelURLEbookIntro from './EditorLeftPanelURLEbookIntro';

const useStyles = makeStyles((theme) => ({
	listItemStyle: {
		backgroundColor: theme.palette.primary[400],
		borderRadius: 4,
		cursor: 'pointer',
		fill: theme.palette.background.paper,
		transition: 'background 0.5s',
		'&:hover': {
			// zIndex: 0,
			backgroundColor: theme.palette.primary[300],
			// opacity: 0.8
		},
	},
	leftPanelWrapper: {},
	gmLogo: {
		width: 150,
	},
	newHeaderElement: {
		fontSize: '1.5rem',
	},
	newBodyElement: {
		fontSize: '1rem',
	},
	line: {
		border: '1px solid #365275',
		width: '100%',
	},
	marginTopStyle: {
		marginTop: theme.spacing(2),
	},
	titleEdit: {
		fontSize: '1rem',
		fontWeight: 'bold',
		paddingTop: theme.spacing(0.5),
	},
	titleEditNoPaddingTop: {
		fontSize: '1rem',
		fontWeight: 'bold',
	},
	backgroundButton: {
		marginTop: 31,
		marginBottom: 22,
	},
	settingsPanel: {
		display: 'flex',
		justifyContent: 'space-evenly',
		alignItems: 'center',
		flexDirection: 'row',
	},
	settingsItem: {
		display: 'flex',
		flex: 1,
		padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
	},
	buttonPanelStyle: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
	},
	iconButtonStyle: {
		justifyContent: 'center',
		fontSize: '1rem',
		color: theme.palette.background.default,
	},
	buttonStyle: {
		justifyContent: 'center',
		fontSize: '1rem',
		backgroundColor: '#365275',
		transition: 'background 0.5s',
		color: theme.palette.background.default,
		'&:hover': {
			backgroundColor: '#365275',
			opacity: 0.8,
		},
	},
	buttonLabelStyle: {
		textTransform: 'initial',
		justifyContent: 'center',
	},
}));

const EditorLeftPanelReport = (props) => {
	const classes = useStyles();
	const { toggleDrawer } = props;
	// const [showingBackground, setShowingBackground] = useState(false);
	const [showingStyle, setShowingStyle] = useState(false);
	const [showingSetting, setShowingSetting] = useState(false);
	const [showingElements, setShowingElements] = useState(true);
	const [reportPreviewing, setReportPreviewing] = useState(false);

	const [showChangeBackgroundDialog, setShowChangeBackgroundDialog] = useState();

	const [changeBackgroundDialogData] = useState({});
	const loadTemplate = useStoreActions((actions) => actions.template.loadTemplate);
	const template = useStoreState((state) => state.template.template);

	// let backgroundColor = comp.getStyle()['background-color'] || ''; // rgb(255, 0, 0)
	// if (backgroundColor) backgroundColor = RGBToHex(backgroundColor);
	// const backgroundColor = '#fff';
	// console.log('generateSections', backgroundColor);
	// const section = {
	// 	// id: `.cover-page`
	// 	id: `#${template}`
	// };

	const doRedoAction = () => {
		window.grapesjs.runCommand('core:redo');
	};

	const doUndoAction = () => {
		window.grapesjs.runCommand('core:undo');
	};

	const handleClickSection = (section) => () => {
		if (section === 'settings') {
			setShowingSetting(true);
			setShowingStyle(false);
			setShowingElements(false);
		} else if (section === 'style') {
			setShowingStyle(true);
			setShowingSetting(false);
			setShowingElements(false);
		}
	};

	useEffect(() => {
		loadTemplate();
	}, []);

	const handleClickBack = () => {
		setShowingElements(true);
		setShowingStyle(false);
		setShowingSetting(false);
	};

	const handleColorChanged = (color) => {
		const { sectionId } = changeBackgroundDialogData;
		setStyleGrapesJSComponent(sectionId, { background: color });
		hideSrcFromImageGrapesJSComponent(sectionId);
	};

	const handleImageChanged = (imageUrl) => {
		const { sectionId } = changeBackgroundDialogData;
		const updatedImageUrl = getResizeImageUrlWidth(imageUrl, 2000);
		setStyleGrapesJSComponent(sectionId, {
			'background-size': 'cover',
			'-webkit-background-size': 'cover',
			'-moz-background-size': 'cover',
			'-o-background-size': 'cover',
			background: `url(${updatedImageUrl}) no-repeat center center;`,
		});
		hideSrcFromImageGrapesJSComponent(sectionId);
	};

	const toggleShowChangeBackgroundDialog = () => {
		setShowChangeBackgroundDialog(!showChangeBackgroundDialog);
	};

	const addText = (section) => () => {
		if (section === 'header') {
			addTextComponentInsideEditor(['new-header-element', 'font-header'], 'header', {
				copyable: false,
				removable: true,
				gm: true,
				draggable: true,
			});
		} else {
			addTextComponentInsideEditor([`new-body-element`], 'body', {
				copyable: false,
				removable: true,
				gm: true,
				draggable: true,
			});
		}
	};

	const addImage = () => {
		addImageComponentInsideEditor(['new-image-element'], {
			copyable: false,
			removable: true,
			draggable: true,
			gm: true,
			type: 'custom-image',
		});
	};

	const renderAddObjectSection = () => {
		return (
			<Box px={1} className={`add-objects-section`}>
				<div className={`${classes.titleEdit} ${classes.marginTopStyle}`}>
					{_e('elements')}
				</div>
				<div className={`flex-column`}>
					<List>
						<ListItem
							onClick={addText('header')}
							classes={{ root: classes.listItemStyle }}
							divider
						>
							<ListItemText>{_e('add_header_text')}</ListItemText>
							<ChevronRightRounded />
						</ListItem>
						<ListItem
							onClick={addText('body')}
							classes={{ root: classes.listItemStyle }}
							divider
						>
							<ListItemText>{_e('add_body_text')}</ListItemText>
							<ChevronRightRounded />
						</ListItem>
						<ListItem onClick={addImage} classes={{ root: classes.listItemStyle }}>
							<ListItemText>{_e('add_image')}</ListItemText>
							<ChevronRightRounded />
						</ListItem>
					</List>
				</div>
			</Box>
		);
	};

	const fullScreen = () => {
		console.log('fullScreen');
		window.grapesjs.runCommand('core:preview');
	};

	const additionClass = 'insideMode';
	const { values, submitForm, setFieldValue } = props;
	const entityReportEbook =
		window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_REPORT_COVER
			? _e('report_cover')
			: _e('introduction_page');
	return (
		<EditorLeftPanelContext.Provider
			value={{
				clickBackCallback: handleClickBack,
				clickFullScreen: fullScreen,
				formik: {
					values,
					submitForm,
					setFieldValue,
				},
			}}
		>
			<div className={`${classes.leftPanelWrapper} left-panel-wrapper ${additionClass}`}>
				<div className="gm-logo-wrapper" style={{ paddingLeft: 16 }}>
					<img
						alt="GoalsMapper - Redefining Financial Planning"
						className="gm-logo"
						src="https://static.goalsmapper.com/public/platform/goalsmapper/gm-brand-white-rectangle.png"
					/>
					<div className={`title-leftpanel`}>
						{window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_REPORT_COVER
							? _e('report_editor', { capitalize: true })
							: _e('intro_page_editor')}
					</div>
				</div>
				<Box className={`leftpanel-content-bottom`}>
					<Box px={1} className={`leftpanel-content`}>
						<div className={classes.buttonPanelStyle}>
							<Tooltip title={_e('undo_last_action')}>
								<IconButton
									onClick={doUndoAction}
									classes={{
										root: classes.iconButtonStyle,
										label: classes.buttonLabelStyle,
									}}
								>
									<svg style={{ width: 15, height: 15 }} viewBox="0 0 24 24">
										<path
											fill="#f2f2f2"
											d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z"
										/>
									</svg>
								</IconButton>
							</Tooltip>
							<Tooltip title={_e('redo_last_action')}>
								<IconButton
									onClick={doRedoAction}
									classes={{
										root: classes.iconButtonStyle,
										label: classes.buttonLabelStyle,
									}}
								>
									<svg style={{ width: 15, height: 15 }} viewBox="0 0 24 24">
										<path
											fill="currentColor"
											d="M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.54,15.22L3.9,16C4.95,12.81 7.95,10.5 11.5,10.5C13.45,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z"
										/>
									</svg>
								</IconButton>
							</Tooltip>
						</div>
						{!showingElements && (
							<div className={`back-button`}>
								<GMButton
									Icon={ArrowBack}
									title={_e('back')}
									onClick={handleClickBack}
								/>
							</div>
						)}
						{showingElements && (
							<div className={classes.settingsPanel}>
								<div className={classes.settingsItem}>
									<Button
										fullWidth
										variant="contained"
										onClick={handleClickSection('style')}
										classes={{
											root: classes.buttonStyle,
											label: classes.buttonLabelStyle,
										}}
									>
										<svg
											style={{ width: 20, height: 20, paddingRight: 5 }}
											viewBox="0 0 24 24"
										>
											<path
												fill="#FFFFFF"
												d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A6,6 0 0,1 16,17H14.2C13.9,17 13.7,17.2 13.7,17.5C13.7,17.6 13.8,17.7 13.8,17.8C14.2,18.3 14.4,18.9 14.4,19.5C14.5,20.9 13.4,22 12,22M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C12.3,20 12.5,19.8 12.5,19.5C12.5,19.3 12.4,19.2 12.4,19.1C12,18.6 11.8,18.1 11.8,17.5C11.8,16.1 12.9,15 14.3,15H16A4,4 0 0,0 20,11C20,7.1 16.4,4 12,4M6.5,10C7.3,10 8,10.7 8,11.5C8,12.3 7.3,13 6.5,13C5.7,13 5,12.3 5,11.5C5,10.7 5.7,10 6.5,10M9.5,6C10.3,6 11,6.7 11,7.5C11,8.3 10.3,9 9.5,9C8.7,9 8,8.3 8,7.5C8,6.7 8.7,6 9.5,6M14.5,6C15.3,6 16,6.7 16,7.5C16,8.3 15.3,9 14.5,9C13.7,9 13,8.3 13,7.5C13,6.7 13.7,6 14.5,6M17.5,10C18.3,10 19,10.7 19,11.5C19,12.3 18.3,13 17.5,13C16.7,13 16,12.3 16,11.5C16,10.7 16.7,10 17.5,10Z"
											/>
										</svg>
										{_e('styles')}
									</Button>
								</div>
								<div className={classes.settingsItem}>
									<Button
										fullWidth
										variant="contained"
										onClick={handleClickSection('settings')}
										classes={{
											root: classes.buttonStyle,
											label: classes.buttonLabelStyle,
										}}
									>
										<svg
											style={{ width: 20, height: 20, paddingRight: 5 }}
											viewBox="0 0 24 24"
										>
											<path
												fill="currentColor"
												d="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10M10,22C9.75,22 9.54,21.82 9.5,21.58L9.13,18.93C8.5,18.68 7.96,18.34 7.44,17.94L4.95,18.95C4.73,19.03 4.46,18.95 4.34,18.73L2.34,15.27C2.21,15.05 2.27,14.78 2.46,14.63L4.57,12.97L4.5,12L4.57,11L2.46,9.37C2.27,9.22 2.21,8.95 2.34,8.73L4.34,5.27C4.46,5.05 4.73,4.96 4.95,5.05L7.44,6.05C7.96,5.66 8.5,5.32 9.13,5.07L9.5,2.42C9.54,2.18 9.75,2 10,2H14C14.25,2 14.46,2.18 14.5,2.42L14.87,5.07C15.5,5.32 16.04,5.66 16.56,6.05L19.05,5.05C19.27,4.96 19.54,5.05 19.66,5.27L21.66,8.73C21.79,8.95 21.73,9.22 21.54,9.37L19.43,11L19.5,12L19.43,13L21.54,14.63C21.73,14.78 21.79,15.05 21.66,15.27L19.66,18.73C19.54,18.95 19.27,19.04 19.05,18.95L16.56,17.95C16.04,18.34 15.5,18.68 14.87,18.93L14.5,21.58C14.46,21.82 14.25,22 14,22H10M11.25,4L10.88,6.61C9.68,6.86 8.62,7.5 7.85,8.39L5.44,7.35L4.69,8.65L6.8,10.2C6.4,11.37 6.4,12.64 6.8,13.8L4.68,15.36L5.43,16.66L7.86,15.62C8.63,16.5 9.68,17.14 10.87,17.38L11.24,20H12.76L13.13,17.39C14.32,17.14 15.37,16.5 16.14,15.62L18.57,16.66L19.32,15.36L17.2,13.81C17.6,12.64 17.6,11.37 17.2,10.2L19.31,8.65L18.56,7.35L16.15,8.39C15.38,7.5 14.32,6.86 13.12,6.62L12.75,4H11.25Z"
											/>
										</svg>
										{_e('settings')}
									</Button>
								</div>
							</div>
						)}
						{/*{showingAllSections && renderShowingAllSections()}*/}
						{showingStyle && (
							<EditorLeftPanelStyle
								subtitle={_e('you_can_customize_section', { entityReportEbook })}
							/>
						)}
						{showingSetting && <EditorLeftPanelResetTemplate />}
						{showingElements && renderAddObjectSection()}
					</Box>
					<div className={`leftpanel-bottom`}>
						{window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_REPORT_COVER && (
							<>
								<div className={classes.line} />
								<EditorLeftPanelPreview
									previewButtonTitle="Preview all reports"
									hasDeviceTypeSelector={false}
									handleOnPreview={() => {
										setReportPreviewing(true);
										toggleDrawer();
									}}
								/>
							</>
						)}

						{window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_EBOOK_INTRO && (
							<>
								<div className={classes.line} />
								<Box px={2} className={`preview-ebook-section`}>
									<div
										className={`${classes.titleEditNoPaddingTop} ${classes.marginTopStyle}`}
									>
										{_e('preview_ebook')}
									</div>
									<EditorLeftPanelURLEbookIntro />
								</Box>
							</>
						)}

						<div className={classes.line} />
						<Box px={2}>
							<TemplateRevisionStatusComponent />
						</Box>
					</div>
				</Box>
				{showChangeBackgroundDialog && (
					<ChangeBackgroundDialog
						open={showChangeBackgroundDialog}
						onColorChanged={handleColorChanged}
						handleClose={toggleShowChangeBackgroundDialog}
						onImageChanged={handleImageChanged}
						changeBackgroundDialogData={changeBackgroundDialogData}
					/>
				)}
				<ReportsPreviewDialog
					template={template}
					open={reportPreviewing}
					onClose={() => {
						setReportPreviewing(false);
						toggleDrawer();
					}}
				/>
			</div>
		</EditorLeftPanelContext.Provider>
	);
};

EditorLeftPanelReport.propTypes = {
	values: PropTypes.object,
	submitForm: PropTypes.func,
	setFieldValue: PropTypes.func,
	toggleDrawer: PropTypes.func,
};

export default EditorLeftPanelReport;
